import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ViolationsTable = ({ violations, onSort, sortConfig, onDelete }) => {
  const getSortIcon = (key) => {
    if (sortConfig?.key !== key) return 'ChevronsUpDown';
    return sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown';
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Attendance': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'Conduct': return 'text-purple-600 bg-purple-50 border-purple-200';
      case 'Performance': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'Administrative': return 'text-gray-600 bg-gray-50 border-gray-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getPointsColor = (points) => {
    if (points <= 5) return 'text-success';
    if (points <= 10) return 'text-warning';
    return 'text-error';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (violations?.length === 0) {
    return (
      <div className="bg-card rounded-lg elevation-sm p-12 text-center">
        <Icon name="AlertCircle" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-foreground mb-2">No Violations Found</h3>
        <p className="text-muted-foreground">Try adjusting your filters to see more results.</p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg elevation-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="text-left p-3 md:p-4">
                <button
                  onClick={() => onSort('employeeName')}
                  className="flex items-center gap-2 font-heading font-semibold text-sm md:text-base text-foreground hover:text-primary transition-smooth"
                >
                  Employee
                  <Icon name={getSortIcon('employeeName')} size={16} />
                </button>
              </th>
              <th className="text-left p-3 md:p-4">
                <button
                  onClick={() => onSort('type')}
                  className="flex items-center gap-2 font-heading font-semibold text-sm md:text-base text-foreground hover:text-primary transition-smooth"
                >
                  Violation Type
                  <Icon name={getSortIcon('type')} size={16} />
                </button>
              </th>
              <th className="text-left p-3 md:p-4">
                <button
                  onClick={() => onSort('category')}
                  className="flex items-center gap-2 font-heading font-semibold text-sm md:text-base text-foreground hover:text-primary transition-smooth"
                >
                  Category
                  <Icon name={getSortIcon('category')} size={16} />
                </button>
              </th>
              <th className="text-left p-3 md:p-4">
                <button
                  onClick={() => onSort('points')}
                  className="flex items-center gap-2 font-heading font-semibold text-sm md:text-base text-foreground hover:text-primary transition-smooth"
                >
                  Points
                  <Icon name={getSortIcon('points')} size={16} />
                </button>
              </th>
              <th className="text-left p-3 md:p-4">
                <button
                  onClick={() => onSort('date')}
                  className="flex items-center gap-2 font-heading font-semibold text-sm md:text-base text-foreground hover:text-primary transition-smooth"
                >
                  Date
                  <Icon name={getSortIcon('date')} size={16} />
                </button>
              </th>
              <th className="text-left p-3 md:p-4">
                <span className="font-heading font-semibold text-sm md:text-base text-foreground">
                  Description
                </span>
              </th>
              <th className="text-right p-3 md:p-4">
                <span className="font-heading font-semibold text-sm md:text-base text-foreground">
                  Actions
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {violations?.map((violation) => (
              <tr
                key={violation?.id}
                className="border-b border-border hover:bg-muted/30 transition-smooth"
              >
                <td className="p-3 md:p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-full overflow-hidden flex-shrink-0">
                      <Image
                        src={violation?.avatar}
                        alt={violation?.avatarAlt}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="min-w-0">
                      <div className="font-body font-medium text-sm md:text-base text-foreground truncate">
                        {violation?.employeeName}
                      </div>
                      <div className="font-caption text-xs text-muted-foreground truncate">
                        {violation?.employeeIdDisplay}
                      </div>
                      <div className="font-caption text-xs text-muted-foreground truncate">
                        {violation?.department}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="p-3 md:p-4">
                  <span className="font-body text-sm md:text-base text-foreground">
                    {violation?.type}
                  </span>
                </td>
                <td className="p-3 md:p-4">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getCategoryColor(violation?.category)}`}>
                    {violation?.category}
                  </span>
                </td>
                <td className="p-3 md:p-4">
                  <span className={`font-data font-semibold text-sm md:text-base ${getPointsColor(violation?.points)}`}>
                    {violation?.points}
                  </span>
                </td>
                <td className="p-3 md:p-4">
                  <span className="font-body text-sm md:text-base text-foreground">
                    {formatDate(violation?.date)}
                  </span>
                </td>
                <td className="p-3 md:p-4">
                  <span className="font-body text-sm text-muted-foreground line-clamp-2">
                    {violation?.description || 'No description provided'}
                  </span>
                </td>
                <td className="p-3 md:p-4">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onDelete(violation?.id)}
                      className="p-2 rounded-lg hover:bg-error/10 transition-smooth"
                      title="Delete Violation"
                    >
                      <Icon name="Trash2" size={18} className="text-error" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ViolationsTable;